import { Box, Flex, Link, Text } from '@chakra-ui/react';
import {
  useGetMeQuery,
  useUpdateAccountPreferenceMutation,
} from '../../../store/redux/api/accountApi';
import { CustomInfoIcon } from '../../../theme/icons/CustomInfoIcon';

interface BuilderBannerProps {
  title?: string;
  subtitle?: string;
  simpleMode?: boolean;
}

const BuilderBanner = ({
  title = 'Welcome to the new pass template builder',
  subtitle = "We've made improvements to help you design and manage templates more easily.",
  simpleMode = false,
}: BuilderBannerProps) => {
  const { data: me, isLoading, isFetching } = useGetMeQuery(undefined);
  const [updateAccountPreference, { isLoading: isUpdating }] =
    useUpdateAccountPreferenceMutation();

  if (isLoading || isFetching || !me) return null;

  const accountEmail = me?.account?.email;
  const emailParam = accountEmail ? `?email=${encodeURIComponent(accountEmail)}` : '';
  const feedbackUrl = simpleMode
    ? `https://tally.so/r/68VV8P${emailParam}`
    : `https://tally.so/r/68ONpP${emailParam}`;

  const isNewBuilderEnabled = !simpleMode;
  const toggleEnabled = simpleMode
    ? false
    : Boolean(me?.builder_toggle_enabled);

  const handleToggleView = async (target: 'true' | 'false') => {
    try {
      await updateAccountPreference({ preference: target }).unwrap();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box
      bg={toggleEnabled ? '#FCF6CC' : '#E3F7FB'}
      w="full"
      mt={4}
      py={isNewBuilderEnabled ? 2 : 3}
      px={4}
      opacity={isUpdating ? 0.6 : 1}
      borderLeft={toggleEnabled ? '10px solid #EFD200' : '10px solid #1868DB'}
    >
      <Flex direction="row" gap={3} alignItems="center">
        <CustomInfoIcon
          boxSize="20px"
          color={toggleEnabled ? '#EFD200' : '#1868DB'}
        />
        <Flex direction={isNewBuilderEnabled || simpleMode ? 'column' : 'row'} gap={0.5}>
          {toggleEnabled ? (
            <>
              {isNewBuilderEnabled ? (
                <>
                  <Text textStyle="bodyFamilySemiBold" fontSize="12px">
                    You're exploring an early-access version of this page
                  </Text>
                  <Text textStyle="bodyFamilyRegular" fontSize="12px">
                    Things may look a little different as we continue testing
                    and improving the experience. Not quite ready? You can{' '}
                    <Link
                      textDecoration="underline"
                      onClick={() => handleToggleView('false')}
                    >
                      go back to the old view
                    </Link>
                  </Text>
                  <Text textStyle="bodyFamilyRegular" fontSize="12px">
                    Share your thoughts on the new builder{' '}
                    <Link
                      textDecoration="underline"
                      href={feedbackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </Link>
                  </Text>
                </>
              ) : (
                <>
                  <Text textStyle="bodyFamilySemiBold" fontSize="12px">
                    You're viewing the classic version of this page
                  </Text>
                  <Text textStyle="bodyFamilyRegular" fontSize="12px" pl={1.5}>
                    Want to try the new builder?{' '}
                    <Link
                      textDecoration="underline"
                      onClick={() => handleToggleView('true')}
                    >
                      Switch to the new view
                    </Link>
                  </Text>
                </>
              )}
            </>
          ) : (
            <>
              <Text textStyle="bodyFamilySemiBold" fontSize="12px">
                {title}
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="12px">
                {subtitle}{' '}
                <Link
                  textDecoration="underline"
                  href={feedbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  textUnderlineOffset="2px"
                >
                  Share feedback
                </Link>
              </Text>
            </>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default BuilderBanner;
